import {
  ingredientForSize,
  ingredientPriceCents,
  parseMoneyToCents,
  sizeCmFromName,
  type PizzaConfig,
  type PizzaIngredientLike,
} from '@agos/shared'

import type { PizzaSizeRow } from './pizza-size-rows'

/**
 * What a dynamic pizza costs at each size it is offered in — FRONTEND.md §9.227.
 *
 * *"and selector so we can see what will be actually the price… vypočítaná cena based on everything
 * selected."*
 *
 * **The same rule the server uses, run on the draft.** `ProductCosts.costOfPizza` computes a size's
 * cost as the size's `baseCost` plus each chosen ingredient's cost *at that size*, and answers a
 * margin against the size's own price. This is that arithmetic against what is on screen — the
 * ingredients ticked a second ago and the prices being typed — because the server can only cost a
 * product that has been **saved**, and the owner asked to see the number *before* saving.
 *
 * **It is a preview and says so.** Nothing here is stored or sent; the server recomputes on save and
 * remains the figure of record. If the two ever disagree, the server is right and this is the bug.
 *
 * **A missing cost is not a zero cost.** An ingredient with no cost at that size, or a configuration
 * with no `baseCost`, makes the whole size's cost `null` — the same distinction §10A.90 draws for a
 * stock item, and for the same reason: **0 of 130 stock items on this data carries a price**, so a
 * confident `0,00 €` here would be the common case and would be wrong.
 */

export interface SizeCosting {
  label: string
  cm: number
  /** Cents, or `null` when something in the sum has no cost. */
  costCents: number | null
  /**
   * What the size sells for, from the typed field — a **fixed**-price pizza's own number.
   *
   * Under dynamic pricing nobody types this; see {@link SizeCosting.computedPriceCents}.
   */
  priceCents: number
  /**
   * `basePrice + Σ ingredient price at this size` — §9.236, legacy's *Vypočítaná cena*.
   *
   * The same sum as `costCents`, run over the prices rather than the costs. `null` when the
   * configuration has no size to price against, which is the one case legacy prints `—` for.
   */
  computedPriceCents: number | null
  /** Percent of the price kept, or `null` without a cost or a price. */
  marginPercent: number | null
  /** How many of the chosen ingredients had a cost at this size, of how many were chosen. */
  withCost: number
  chosen: number
}

export function sizeCosting(
  config: Pick<PizzaConfig, 'sizes'> | null,
  rows: readonly PizzaSizeRow[],
  ingredients: readonly PizzaIngredientLike[] | undefined,
  selectedIds: readonly number[],
): SizeCosting[] {
  if (config === null) return []

  const byId = new Map<number, PizzaIngredientLike>()
  for (const ingredient of ingredients ?? []) {
    const id = (ingredient as { id?: number }).id
    if (typeof id === 'number') byId.set(id, ingredient)
  }

  return rows
    .filter((row) => row.offered !== false)
    .map((row) => {
      const cm = sizeCmFromName(row.label) ?? 0
      const configured = (config.sizes ?? []).find((size) => size.cm === cm)
      const baseCost = configured?.baseCost

      let toppings = 0
      let withCost = 0
      let anyMissing = false
      for (const id of selectedIds) {
        const ingredient = byId.get(id)
        const cost = ingredient === undefined ? null : ingredientForSize(ingredient, cm).cost
        if (cost === null || cost === undefined) {
          anyMissing = true
          continue
        }
        toppings += cost
        withCost += 1
      }

      // The base is not optional: without it there is no floor to add toppings to.
      const costCents =
        baseCost === undefined || baseCost === null || anyMissing
          ? null
          : Math.round((baseCost + toppings) * 100)

      const priceCents = parseMoneyToCents(row.price)

      // §9.236 — the price a dynamic pizza actually sells at, which is not typed anywhere.
      let toppingPrice = 0
      for (const id of selectedIds) {
        const ingredient = byId.get(id)
        if (ingredient !== undefined) toppingPrice += ingredientPriceCents(ingredient, cm)
      }
      const computedPriceCents =
        configured === undefined ? null : Math.round(configured.basePrice * 100) + toppingPrice

      // **Measured against the computed price, not the typed one.** A margin on a dynamic pizza is
      // the margin on what it sells for, and what it sells for is the sum above.
      const against = computedPriceCents ?? priceCents
      const marginPercent =
        costCents === null || against <= 0 ? null : ((against - costCents) / against) * 100

      return {
        label: row.label,
        cm,
        costCents,
        priceCents,
        computedPriceCents,
        marginPercent,
        withCost,
        chosen: selectedIds.length,
      }
    })
}
